import { FC, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";
import { FlexDiv } from "components/Layout/FlexDiv";
import { ProspectInfoWrapper } from "./styles";
import { ChipInput } from "components/ChipInput";
import { UserInfoProps } from "./type";
import { ActiveSwitch, AddCreditWrapper } from "../SubscriptionInfo/styles";
import { useConfirmModal } from "context/confirmModal";
import useDeleteUser from "hooks/services/user/useDeleteUser";
import { useConditionalSnackBar } from "context/snackbar";
import { CronoButton } from "components/CronoButton";
import { User } from "types/user";
import useResetPassword from "hooks/services/subscription/useResetPassword";
import Role from "types/enums/role";
import usePatchUser from "hooks/services/user/usePatchUser";
import { useAuth } from "context/auth";
import { getError } from "utils";
import { Close, ContentCopy, OpenInNewOutlined } from "@mui/icons-material";
import { createPortal } from "react-dom";
import useAddPhoneNumber from "hooks/services/phoneNumber/useAddPhoneNumber";
import { Subscription } from "types/subscription";

// A multi-customer user without a date of their own expires with the subscription
const getEffectiveExpirationDate = (
  user: User | null,
  subscription: Subscription | null,
) => {
  const date = user?.expirationDate ?? subscription?.expirationDate;
  return date ? moment.utc(date).hour(12).toDate() : null;
};

const UserInfo: FC<UserInfoProps> = ({ user, subscription }) => {
  const { admin } = useAuth();
  const [firstName, setFirstName] = useState<string>(user?.firstName || "");
  const [lastName, setLastName] = useState<string>(user?.lastName || "");
  const [email, setEmail] = useState<string>(user?.email || "");
  const [active, setActive] = useState<boolean>(user?.active || false);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(
    user.userRoles ?? [Role.BASIC],
  );
  const [showCreatePhoneNumber, setShowCreatePhoneNumber] =
    useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [sid, setSid] = useState<string>("");
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    getEffectiveExpirationDate(user, subscription),
  );
  const [expirationDatePickerOpen, setExpirationDatePickerOpen] =
    useState<boolean>(false);

  const handleCopyUserId = () => {
    if (!user) return;
    navigator.clipboard.writeText(String(user.id)).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    });
  };

  const { openModal: openConfirmModal } = useConfirmModal();

  const {
    mutate: patchUser,
    isSuccess: isPatchSuccess,
    error: patchError,
  } = usePatchUser();

  const {
    mutate: deleteUser,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
  } = useDeleteUser();

  const {
    mutate: resetPassword,
    isSuccess: isResetSuccess,
    isError: isResetError,
  } = useResetPassword();

  const {
    mutate: addPhoneNumber,
    isLoading: isLoadingAddPhoneNumber,
    isSuccess: isSuccessAddPhoneNumber,
    error: addPhoneNumberError,
  } = useAddPhoneNumber();

  useEffect(() => {
    setFirstName(user?.firstName || "");
    setLastName(user?.lastName || "");
    setEmail(user?.email || "");
    setActive(user?.active || false);
    setExpirationDate(getEffectiveExpirationDate(user, subscription));
  }, [user, subscription]);

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextStatus = e.target.checked;
    openConfirmModal({
      title: "Are you sure you want to disable the following user?",
      text: "User: " + user?.email + " will be disabled",
      cancelText: "No",
      confirmText: "Yes",
      confirmFunction: () => {
        if (user && subscription && user.active) {
          openConfirmModal({
            title: "Delete permanently?",
            text: "Data for: " + user?.email + " will be deleted forever",
            cancelText: "No",
            confirmText: "Yes",
            confirmFunction: () => {
              setActive(nextStatus);
              deleteUser({
                delete: true,
                userId: user.id,
                subscriptionId: subscription?.id,
              });
            },
            cancelFunction: () => {
              setActive(nextStatus);
              deleteUser({
                delete: false,
                userId: user.id,
                subscriptionId: subscription?.id,
              });
            },
          });
        }
      },
      cancelFunction: () => null,
    });
  };

  const handleChangeRoles = (event: SelectChangeEvent<Role[]>) => {
    const value = event.target.value as Role[];
    setSelectedRoles(value);
    patchUser({
      userRoles: value,
      userId: user.id,
      subscriptionId: subscription?.id,
    });
  };

  const handleChangeFirstName = () => {
    if (firstName === (user?.firstName || "")) return;
    patchUser({
      firstName: firstName,
      userId: user.id,
      subscriptionId: subscription?.id,
    });
  };

  const handleChangeLastName = () => {
    if (lastName === (user?.lastName || "")) return;
    patchUser({
      lastName: lastName,
      userId: user.id,
      subscriptionId: subscription?.id,
    });
  };
  const handleChangeEmail = () => {
    if (email === (user?.email || "")) return;
    patchUser({
      email: email,
      userId: user.id,
      subscriptionId: subscription?.id,
    });
  };

  const handleChangeExpirationDate = (value: Date | null) => {
    if (!value || isNaN(value.getTime())) return;
    const subscriptionExpirationDate = subscription?.expirationDate
      ? moment.utc(subscription.expirationDate)
      : null;
    const extendsSubscription =
      !!subscriptionExpirationDate &&
      moment(value).format("YYYY-MM-DD") >
        subscriptionExpirationDate.format("YYYY-MM-DD");
    openConfirmModal({
      title: "Are you sure you want to change the expiration date?",
      text:
        "New expiration date for " +
        user?.email +
        ": " +
        moment(value).format("MM/DD/YYYY") +
        (extendsSubscription
          ? ". The subscription expiration date will be moved to the same day."
          : ""),
      cancelText: "No",
      confirmText: "Yes",
      confirmFunction: () => {
        setExpirationDate(value);
        patchUser({
          expirationDate: moment(value).format("YYYY-MM-DD"),
          userId: user.id,
          subscriptionId: subscription?.id,
        });
      },
      cancelFunction: () => null,
    });
  };

  useConditionalSnackBar([
    {
      condition: !!isDeleteError,
      message: "Error while disabling the user",
      severity: "error",
    },
    {
      condition: !!isDeleteSuccess,
      message: "User disabled successfully",
      severity: "success",
    },
    {
      condition: !!patchError,
      message: getError(patchError) ?? "Error while updating user information",
      severity: "error",
    },
    {
      condition: !!isPatchSuccess,
      message: "User information updated successfully",
      severity: "success",
    },
    {
      condition: !!isResetError,
      message: "Error while resetting the password",
      severity: "error",
    },
    {
      condition: !!isResetSuccess,
      message: "Password reset successfully",
      severity: "success",
    },
    {
      condition: !!isSuccessAddPhoneNumber,
      message: "Phone number added successfully",
      severity: "success",
    },
    {
      condition: !!addPhoneNumberError,
      message:
        getError(addPhoneNumberError) ?? "Error while adding the phone number",
      severity: "error",
    },
    {
      condition: copySuccess,
      message: "User Id copied to clipboard",
      severity: "success",
    },
  ]);

  if (!user) {
    return (
      <FlexDiv>
        <CircularProgress />
      </FlexDiv>
    );
  }

  const resetPasswordRequest = (user: User) => {
    openConfirmModal({
      title: "Are you sure you want to reset the password?",
      text: "A new password will be sent to: " + user?.email,
      cancelText: "No",
      confirmText: "Yes",
      confirmFunction: () => {
        if (user && subscription) {
          resetPassword({
            userId: user.id,
            subscriptionId: subscription?.id,
          });
        }
      },
      cancelFunction: () => null,
    });
  };

  const handleAddPhoneNumber = () => {
    if (user && subscription) {
      addPhoneNumber({
        userId: user.id,
        subscriptionId: subscription.id,
        phoneNumber: phoneNumber,
        sid: sid,
      });
      setShowCreatePhoneNumber(false);
      setPhoneNumber("");
      setSid("");
    }
  };

  return (
    <ProspectInfoWrapper>
      {showCreatePhoneNumber &&
        createPortal(
          <AddCreditWrapper
            id="add-credit-wrapper"
            onClick={() => {
              setShowCreatePhoneNumber(false);
            }}
          >
            <div
              className="container"
              onClick={(ev) => {
                ev.stopPropagation();
                ev.preventDefault();
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography fontSize={24} lineHeight={"30px"} fontWeight={700}>
                  Add Phone Number
                </Typography>
                <Close
                  style={{
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setShowCreatePhoneNumber(false);
                  }}
                />
              </div>
              <TextField
                variant="standard"
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(ev) => {
                  setPhoneNumber(ev.target.value);
                }}
              />
              <TextField
                variant="standard"
                value={sid}
                placeholder="SID"
                onChange={(ev) => {
                  setSid(ev.target.value);
                }}
              />
              <FlexDiv justifyContent="end">
                {isLoadingAddPhoneNumber ? (
                  <CircularProgress />
                ) : (
                  <Button
                    variant="contained"
                    className="submit-add-button"
                    disabled={!phoneNumber || !sid || !subscription || !user}
                    onClick={handleAddPhoneNumber}
                  >
                    Add
                  </Button>
                )}
              </FlexDiv>
            </div>
          </AddCreditWrapper>,
          document.body,
        )}
      <div className="prospect-info-row">
        <p>Active</p>
        <ActiveSwitch
          id="active"
          name="active"
          checked={active}
          onChange={user?.active ? handleActiveChange : () => null}
        />
      </div>
      <div className="prospect-info-row">
        <p>First name</p>
        <ChipInput
          className="prospect-info-chip"
          textAlign={"center"}
          id="firstName"
          name="firstName"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          onKeyDown={(event: any) => {
            if (event.key === "Enter") {
              //The onBlur will call the API
              event.target.blur();
            }
          }}
          onBlur={handleChangeFirstName}
          disabled={!admin?.canModify}
        />
      </div>
      <div className="prospect-info-row">
        <p>Last name</p>
        <ChipInput
          className="prospect-info-chip"
          textAlign={"center"}
          id="lastName"
          name="lastName"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
          onKeyDown={(event: any) => {
            if (event.key === "Enter") {
              //The onBlur will call the API
              event.target.blur();
            }
          }}
          onBlur={handleChangeLastName}
          disabled={!admin?.canModify}
        />
      </div>
      <div className="prospect-info-row">
        <p>E-Mail</p>
        <FlexDiv>
          <ChipInput
            className="prospect-info-chip"
            textAlign={"center"}
            id="email"
            name="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event: any) => {
              if (event.key === "Enter") {
                //The onBlur will call the API
                event.target.blur();
              }
            }}
            onBlur={handleChangeEmail}
            disabled={!admin?.canModify}
          />
        </FlexDiv>
      </div>
      <div className="prospect-info-row">
        <p>User Id</p>
        <FlexDiv>
          <ChipInput
            className="prospect-info-chip"
            textAlign={"center"}
            id="id"
            name="id"
            value={user.id}
          />
          <Tooltip title="Copy User Id">
            <IconButton size="small" onClick={handleCopyUserId}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </FlexDiv>
      </div>
      <div className="prospect-info-row">
        <p>Integration</p>
        <FlexDiv>
          <ChipInput
            className="prospect-info-chip"
            textAlign={"center"}
            id="integration"
            name="integration"
            value={
              user.firstIntegration
                ? "First import done"
                : user.integrationType
                  ? "Integrated "
                  : "Not integrated"
            }
          />
        </FlexDiv>
      </div>
      <div className="prospect-info-row">
        <p>Last Login</p>
        <FlexDiv>
          <ChipInput
            className="prospect-info-chip"
            textAlign={"center"}
            id="lastLogin"
            name="lastLogin"
            value={
              user.lastLogin ? user.lastLogin.toString().split("T")[0] : "Never"
            }
          />
        </FlexDiv>
      </div>
      {subscription?.multiCustomer && (
        <div className="prospect-info-row">
          <p>Expiration Date</p>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={expirationDate}
              inputFormat="dd/MM/yyyy"
              disabled={!admin?.canModify}
              open={expirationDatePickerOpen}
              onChange={(value) => handleChangeExpirationDate(value)}
              onClose={() => setExpirationDatePickerOpen(false)}
              renderInput={(params) => (
                <TextField
                  className="input-date"
                  fullWidth
                  {...params}
                  disabled
                  onClick={() => {
                    if (admin?.canModify) setExpirationDatePickerOpen(true);
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </div>
      )}
      <div className="prospect-info-row">
        <p>Linkedin Profile</p>
        <FlexDiv>
          <ChipInput
            className="prospect-info-chip"
            textAlign={"center"}
            id="linkedin"
            name="linkedin"
            value={
              user.linkedinSettings
                ? user.linkedinSettings?.publicId
                : "Not connected"
            }
            disabled={true}
          />
          {user.linkedinSettings?.publicId && (
            <OpenInNewOutlined
              onClick={() => {
                if (user.linkedinSettings) {
                  window.open(
                    "https://www.linkedin.com/in/" +
                      user.linkedinSettings?.publicId,
                    "_blank",
                  );
                }
              }}
              style={{
                cursor: "pointer",
              }}
            />
          )}
        </FlexDiv>
      </div>
      <div className="prospect-info-row">
        <p>Roles</p>
        <Select
          multiple
          value={selectedRoles}
          onChange={handleChangeRoles}
          renderValue={(selected) => (selected as Role[]).join(", ")}
          variant="standard"
          disableUnderline
          sx={{
            backgroundColor: "#f8f9fd",
            borderRadius: "20px",
            height: "40px",
            padding: "5px 10px",
            maxWidth: 200,
            width: "100%",
            fontSize: "14px",
            textAlign: "center",
            "& .MuiSelect-select": {
              padding: "0 !important",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            },
          }}
        >
          {Object.values(Role).map((role) => (
            <MenuItem key={role} value={role}>
              <Checkbox checked={selectedRoles.includes(role)} size="small" />
              <ListItemText primary={role} />
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className="prospect-info-row">
        <p>Password</p>
        <FlexDiv>
          <CronoButton
            variant="contained"
            color="primary"
            className="mail-button"
            onClick={() => resetPasswordRequest(user)}
          >
            Reset
          </CronoButton>
        </FlexDiv>
      </div>
      <div className="prospect-info-row">
        <p>Add Phone Number</p>
        <FlexDiv>
          <CronoButton
            variant="contained"
            color="primary"
            className="mail-button"
            onClick={() => setShowCreatePhoneNumber(true)}
          >
            Create
          </CronoButton>
        </FlexDiv>
      </div>
    </ProspectInfoWrapper>
  );
};

export default UserInfo;
